import React, { useState, useRef, useEffect } from 'react';
import { Calendar, ChevronDown } from 'lucide-react';
import { usePeriod } from '../../contexts/PeriodContext';

export const PeriodSelector: React.FC = () => {
  const { periods, currentPeriod, selectedPeriod, setSelectedPeriod, loading } = usePeriod();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (loading || periods.length <= 1) return null;

  const isAll = selectedPeriod === 'all';
  const selected = isAll ? null : periods.find(p => p.id === selectedPeriod) || currentPeriod;

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-full border border-gray-200 bg-white hover:bg-gray-50 transition-colors"
      >
        <Calendar className="w-3.5 h-3.5 text-gray-500" />
        <span className="text-gray-700">{isAll ? 'Todos' : (selected?.id || '...')}</span>
        {!isAll && selected?.is_current && (
          <span className="px-1.5 py-0.5 text-[10px] font-semibold bg-green-100 text-green-700 rounded-full">
            Actual
          </span>
        )}
        <ChevronDown className="w-3.5 h-3.5 text-gray-400" />
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-1 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
          {periods.map(period => (
            <button
              key={period.id}
              onClick={() => {
                setSelectedPeriod(period.id);
                setIsOpen(false);
              }}
              className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center justify-between ${
                period.id === selectedPeriod ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
              }`}
            >
              <div>
                <div className="font-medium">{period.id}</div>
                <div className="text-xs text-gray-500">{period.name}</div>
              </div>
              {period.is_current ? (
                <span className="px-1.5 py-0.5 text-[10px] font-semibold bg-green-100 text-green-700 rounded-full">
                  Actual
                </span>
              ) : (
                <span className="px-1.5 py-0.5 text-[10px] font-semibold bg-gray-100 text-gray-500 rounded-full">
                  Archivo
                </span>
              )}
            </button>
          ))}
          <div className="border-t border-gray-100 mt-1 pt-1">
            <button
              onClick={() => {
                setSelectedPeriod('all');
                setIsOpen(false);
              }}
              className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center justify-between ${
                isAll ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
              }`}
            >
              <div>
                <div className="font-medium">Todos los periodos</div>
                <div className="text-xs text-gray-500">Ver datos combinados</div>
              </div>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
